import React, { createContext, useContext, useState, useCallback } from 'react';
import ToastContainer from '../components/toast/ToastContainer';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((toast) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast = {
      id,
      type: 'info',
      duration: 5000, // 5 seconds default
      ...toast
    };
    
    setToasts(prev => [...prev, newToast]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const toast = {
    success: (message, options = {}) => {
      return addToast({
        type: 'success',
        message,
        title: options.title || 'Success',
        duration: options.duration || 4000,
        ...options
      });
    },
    error: (message, options = {}) => {
      return addToast({
        type: 'error',
        message,
        title: options.title || 'Error',
        duration: options.duration || 6000,
        ...options
      });
    },
    warning: (message, options = {}) => {
      return addToast({
        type: 'warning',
        message,
        title: options.title || 'Warning',
        duration: options.duration || 5000,
        ...options
      });
    },
    info: (message, options = {}) => {
      return addToast({
        type: 'info',
        message,
        title: options.title || 'Info',
        duration: options.duration || 4000,
        ...options
      });
    },
    loading: (message, options = {}) => {
      return addToast({
        type: 'loading',
        message,
        title: options.title || 'Loading',
        duration: 0, // Loading toasts don't auto-dismiss
        ...options
      });
    },
    // For compatibility with react-hot-toast API
    default: (message, options = {}) => {
      return addToast({
        type: 'info',
        message,
        title: options.title || 'Info',
        duration: options.duration || 4000,
        ...options
      });
    },
    // Custom toast
    custom: (toastData) => {
      return addToast(toastData);
    },
    // Dismiss specific toast
    dismiss: (id) => {
      removeToast(id);
    },
    // Clear all toasts
    clear: () => {
      clearAllToasts();
    }
  };

  return (
    <ToastContext.Provider value={{ toast, toasts, addToast, removeToast, clearAllToasts }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};
